import { catchAsync } from './index.js';
import BaseAPI from './base.js';

class IngredientAPIService extends BaseAPI {
    constructor() {
        super({
            baseUrl: process.env.REACT_APP_API_URL,
            cacheKeyPattern: 'ingredient-${method}-${url}',
            cacheExpiration: 10 * 60 * 1000, // 10 minutes
            debug: false // Enable debugging by default
        });
    }

    getByLetterPage = catchAsync(async (letter, page) => {
        return this.request(`/ingredients?letter=${letter}&page=${page}`);
    });

    search = catchAsync(async (keyword, page = 1) => {
        return this.request(
            `/search/ingredients?keyword=${encodeURIComponent(keyword)}&page=${page}`
        );
    });

    getOne = catchAsync(async (uri) => {
        return this.request(`/ingredients/${uri}`);
    });

    getTradeNamesForIngredient = catchAsync(async (uri, page = 1) => {
        return this.request(`/ingredients/${uri}/tradenames?page=${page}`);
    });

    getTopBrandsForIngredient = catchAsync(async (uri) => {
        return this.request(`/ingredients/${uri}/tradenames-top-brands`);
    });

    getWellReviewedForIngredient = catchAsync(async (uri) => {
        return this.request(`/ingredients/${uri}/tradenames-well-reviewed`);
    });

    /**
     * Search for products by multiple ingredients with AND/OR mode.
     * Uses the dedicated server-side endpoint that performs the intersection/union
     * in the database and returns paginated results in a single request.
     * 
     * @param {string[]} uris - Array of ingredient URIs to search for
     * @param {'and'|'or'} mode - Combination mode: 'and' returns products with ALL ingredients, 'or' returns products with ANY ingredient
     * @param {number} page - Page number for pagination (1-indexed)
     * @param {Object} options - Additional options
     * @param {AbortSignal} options.signal - AbortController signal for timeout handling
     * @returns {Promise<{products: Array, pagination: Object}>} Products and pagination info
     */
    searchByIngredients = catchAsync(async (uris, mode = 'and', page = 1, options = {}) => {
        const params = new URLSearchParams({
            page: String(page),
            mode,
            uris: uris.join(',')
        });
        const response = await this.request(`/ingredients/search-combined?${params.toString()}`, {
            signal: options.signal
        });

        return {
            products: response.products || [],
            pagination: response.pagination || { page, totalItems: 0, totalPages: 0 }
        };
    });

    /**
     * Test cache functionality
     * @returns {Promise<void>}
     */
    async testCache() {
        console.log('=== Starting Cache Test ===');
        
        // Test 1: First request (should hit API)
        console.log('\nTest 1: First request (should hit API)');
        const firstResult = await this.getOne('aspirin');
        console.log('First request result:', firstResult);

        // Test 2: Second request (should hit cache)
        console.log('\nTest 2: Second request (should hit cache)');
        const secondResult = await this.getOne('aspirin');
        console.log('Second request result:', secondResult);

        // Test 3: Different endpoint (should hit API)
        console.log('\nTest 3: Different endpoint (should hit API)');
        const searchResult = await this.search('aspirin');
        console.log('Search result:', searchResult);

        // Test 4: Cache statistics
        console.log('\nTest 4: Cache Statistics');
        const stats = this.getCacheStats();
        console.log('Cache stats:', stats);

        console.log('\n=== Cache Test Complete ===');
    }
}

const IngredientAPI = new IngredientAPIService();
export default IngredientAPI;